<template>
  <div class="text-field__container" :class="containerClass">
    <label :for="id">{{ label }}</label>
    <div class="text-field__container-inner">
      <NsIcon v-if="icon" :icon-name="icon" class="inner-icon" />
      <input
        :id="id"
        ref="input"
        v-model="inputValue"
        :type="type"
        :disabled="disabled"
        :placeholder="placeholder"
        :name="name"
        :aria-describedby="id"
        v-on="inputListeners"
      />
      <button
        v-if="clearable && inputValue.length"
        class="clear-button"
        @click="clearInput"
      >
        <NsIcon icon-name="icon-circle-close-light" />
      </button>
    </div>
    <div v-if="this.$slots.default" class="helper-text">
      <!-- @slot The default slot is used to include any helper-text -->
      <slot name="default"></slot>
    </div>
  </div>
</template>

<script>
/**
 * Original Author: Aaron Turnblom
 * Original Team: Area 51
 */
// Using `import uuid from 'uuid/v4';` will sometimes cause an error when
// @nuskin/design-components is being imported into another package.
import { v4 as uuid } from 'uuid';
import { NsIcon } from '@nuskin/ns-icon';

export default {
  components: {
    NsIcon,
  },
  props: {
    label: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    id: {
      type: String,
      default: () => uuid(),
    },
    icon: {
      type: String,
      default: null,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      inputValue: this.value,
    };
  },
  computed: {
    containerClass: (vm) =>
      vm.disabled ? 'text-field__container--disabled' : '',
    inputListeners: (vm) => {
      return Object.assign({}, vm.$listeners, {
        input(evt) {
          vm.inputValue = evt.target.value;
        },
      });
    },
  },
  watch: {
    inputValue(val) {
      /**
       * The input event, handled for v-model support.
       *
       * @event input
       * @type {string}
       */
      this.$emit('input', val);
    },
    value(val) {
      this.inputValue = val;
    },
  },
  methods: {
    clearInput() {
      this.inputValue = '';
      this.$refs.input.focus();
      this.$emit('clear');
    },
  },
};
</script>

<style scoped lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

.helper-text {
  width: 100%;
  margin-top: 6px;
  color: var(--dryink);
  font-size: 0.875rem;
}
.text-field__container--disabled .helper-text {
  color: var(--eyore);
}
label {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  display: inline-block;
  margin-bottom: 6px;
  line-height: 16px !important;
}
input[type='text'] {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}
.text-field__container--disabled {
  label {
    color: var(--eyore);
  }
  :-ms-input-placeholder {
    color: var(--eyore);
  }
  ::-ms-input-placeholder {
    color: var(--eyore);
  }
  ::placeholder {
    color: var(--eyore);
  }
  .helper-text {
    color: var(--eyore);
  }
}
.text-field__container-inner {
  position: relative;
  display: flex;
  align-items: center;
}
.inner-icon {
  height: 1.5rem;
  width: 1.5rem;
  position: absolute;
  left: 1rem;
}
.inner-icon + input {
  padding-left: 3.5rem;
}
.clear-button {
  position: absolute;
  height: 1.5rem;
  width: 1.5rem;
  right: 1rem;
}
</style>
